import React, { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera,
  ImagePlus,
  Loader2,
  Trash2,
  Star,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { useSession } from "../context/SessionContext";
import { uploadPhoto, deletePhoto, photoUrl, startAssessment, labelPhoto } from "../lib/api";

const AREA_OPTIONS = [
  "Same area as primary photo", "Front Yard", "Backyard", "Side Yard – North", "Side Yard – South",
  "Side Yard – East", "Side Yard – West", "Close-up / Detail", "Other",
];

export const DIAGNOSE_SHOTS = [
  "Entire plant or affected area",
  "Wider view of surrounding environment",
  "Close-up of symptoms",
  "Leaves (upper & lower surfaces)",
  "Trunk, stem or branches",
  "Visible insects or pests",
  "Soil / root zone",
];

export default function Upload() {
  const navigate = useNavigate();
  const { session, setSession } = useSession();
  const primaryInput = useRef(null);
  const extraInput = useRef(null);
  const guidedInput = useRef(null);
  const guidedCategory = useRef(null);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);

  if (!session) return <Navigate to="/" replace />;

  const pathway = session.pathway;
  const toneBtn =
    pathway === "diagnose"
      ? "bg-sky-600 hover:bg-sky-700"
      : "bg-emerald-600 hover:bg-emerald-700";

  const primary = (session.photos || []).find((p) => p.is_primary);
  const extras = (session.photos || []).filter((p) => !p.is_primary);

  const handleFiles = async (files, isPrimary, inputRef, category = null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      // When replacing a primary, delete the old one on the backend first.
      let currentPhotos = [...(session.photos || [])];
      if (isPrimary && primary) {
        try {
          await deletePhoto(session.id, primary.id);
          currentPhotos = currentPhotos.filter((p) => p.id !== primary.id);
        } catch {
          // non-fatal — continue
        }
      }

      const newMetas = [];
      for (const file of files) {
        const markPrimary = isPrimary && files.length === 1;
        const meta = await uploadPhoto(session.id, file, markPrimary, category);
        newMetas.push(meta);
      }

      // If primary just set, make sure prior primaries are demoted locally
      if (isPrimary) {
        currentPhotos = currentPhotos.map((p) => ({ ...p, is_primary: false }));
      }

      setSession({ ...session, photos: [...currentPhotos, ...newMetas] });
      toast.success(
        isPrimary ? "Primary photo saved." : `Added ${files.length} photo(s).`,
      );
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setBusy(false);
      // Reset the input so the same file can be re-selected after remove.
      if (inputRef?.current) inputRef.current.value = "";
    }
  };

  const setLabel = async (photoId, category) => {
    try {
      await labelPhoto(session.id, photoId, category);
      setSession({ ...session, photos: session.photos.map((p) => (p.id === photoId ? { ...p, category } : p)) });
    } catch {
      toast.error("Could not save the photo area.");
    }
  };

  const remove = async (photoId) => {
    try {
      await deletePhoto(session.id, photoId);
      setSession({
        ...session,
        photos: session.photos.filter((p) => p.id !== photoId),
      });
    } catch {
      toast.error("Could not remove photo.");
    }
  };

  const next = async () => {
    if (!primary && extras.length === 0) {
      toast.error("Please add at least one photo before continuing.");
      return;
    }
    setStarting(true);
    try {
      await startAssessment(session.id);
      setSession({ ...session, assessment: null, assessment_status: "processing" });
      navigate(pathway === "diagnose" ? "/diagnose" : "/improve");
    } catch (ex) {
      if (ex?.response?.status === 402) {
        setSession({ ...session, assessment: null, assessment_status: null });
        navigate("/diagnose");
        return;
      }
      toast.error(ex?.response?.data?.detail || "Could not start the assessment.");
    } finally {
      setStarting(false);
    }
  };

  const addGuided = (cat) => {
    guidedCategory.current = cat;
    guidedInput.current?.click();
  };

  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]">
      <BrandHeader
        back
        title="Upload Photos"
        subtitle={pathway === "diagnose" ? "Diagnose My Landscape" : "Improve My Landscape"}
      />
      <main className="mx-auto max-w-xl px-5 py-6 gf-fade-in">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#092C15] leading-tight">
          Snap your landscape
        </h1>
        <p className="mt-1 text-slate-600 text-sm">
          Add one clear primary photo of the full area, then any close-ups.
          Take new photos or choose from your photo library.
        </p>

        {/* Primary photo */}
        <section className="mt-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
            Primary Photo
          </div>

          <input
            ref={primaryInput}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="primary-photo-input"
            onChange={(e) => handleFiles(e.target.files, true, primaryInput, pathway === "diagnose" ? DIAGNOSE_SHOTS[0] : "Primary landscape photo")}
          />

          {primary ? (
            <div className="mt-2 relative rounded-2xl overflow-hidden border border-emerald-200 bg-white">
              <img
                data-testid="primary-photo-preview"
                src={photoUrl(primary.id)}
                alt="Primary landscape"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-emerald-600 text-white rounded-full px-2 py-0.5 text-[11px] font-semibold">
                <Star size={12} /> Primary
              </div>
              <div className="absolute bottom-2 right-2 flex gap-2">
                <button
                  data-testid="primary-photo-replace"
                  onClick={() => primaryInput.current?.click()}
                  className="rounded-full bg-white/95 text-slate-800 px-3 py-1.5 text-xs font-semibold shadow hover:bg-white"
                >
                  Replace
                </button>
                <button
                  data-testid="primary-photo-remove"
                  onClick={() => remove(primary.id)}
                  className="rounded-full bg-rose-600 text-white px-3 py-1.5 text-xs font-semibold shadow hover:bg-rose-700"
                >
                  <Trash2 size={12} className="inline mr-1" /> Remove
                </button>
              </div>
            </div>
          ) : (
            <button
              data-testid="primary-photo-add"
              onClick={() => primaryInput.current?.click()}
              disabled={busy}
              className="mt-2 w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-emerald-300 bg-white hover:bg-emerald-50 transition grid place-items-center text-emerald-700"
            >
              <div className="text-center">
                <Camera className="mx-auto" size={34} />
                <div className="mt-2 font-semibold">Tap to add primary photo</div>
                <div className="text-xs text-slate-500 mt-1">
                  Camera or photo library
                </div>
              </div>
            </button>
          )}
        </section>

        {pathway === "diagnose" && (
          <section className="mt-8" data-testid="guided-shots-section">
            <div className="text-[11px] uppercase tracking-widest font-bold text-sky-700">
              Recommended Photos for Dr. Verdant™
            </div>
            <p className="mt-1 text-xs text-slate-500">
              The more angles you provide, the more careful the assessment. Add what applies.
            </p>
            <input
              ref={guidedInput}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="guided-photo-input"
              onChange={(e) => handleFiles(e.target.files, false, guidedInput, guidedCategory.current)}
            />
            <ol className="mt-3 space-y-2">
              {DIAGNOSE_SHOTS.map((cat, i) => {
                const count = (session.photos || []).filter((p) => p.category === cat).length;
                return (
                  <li key={cat} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-3 py-2.5">
                    <span className={`h-7 w-7 shrink-0 rounded-full grid place-items-center text-xs font-bold ${count ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700"}`}>
                      {count ? <CheckCircle2 size={14} /> : i + 1}
                    </span>
                    <span className="flex-1 text-sm text-slate-700">{cat}</span>
                    {count > 0 && <span className="text-xs text-slate-500">{count}</span>}
                    <button
                      data-testid={`guided-add-${i + 1}`}
                      onClick={() => addGuided(cat)}
                      disabled={busy}
                      className="rounded-full border border-sky-300 text-sky-700 hover:bg-sky-50 px-3 py-1 text-xs font-semibold transition-colors"
                    >
                      <ImagePlus size={12} className="inline mr-1" /> Add
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* Additional photos */}
        <section className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest font-bold text-sky-700">
              Additional Photos
            </div>
            <span className="text-xs text-slate-500">{extras.length} added</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">Pick the area under each photo (for example Backyard or Side Yard – North) so Dr. Verdant™ knows where it was taken.</p>

          <input
            ref={extraInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="extra-photos-input"
            onChange={(e) => handleFiles(e.target.files, false, extraInput)}
          />

          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {extras.map((p) => (
              <div
                key={p.id}
                data-testid={`extra-photo-${p.id}`}
                className="relative rounded-xl overflow-hidden border border-slate-200 bg-white"
              >
                <img
                  src={photoUrl(p.id)}
                  alt="Landscape detail"
                  className="w-full aspect-square object-cover"
                />
                {p.category && !AREA_OPTIONS.includes(p.category) && (
                  <span className={`absolute inset-x-0 bg-black/60 text-white text-[9px] leading-tight px-1.5 py-1 pr-6 truncate ${DIAGNOSE_SHOTS.includes(p.category) ? "bottom-0" : "top-0"}`}>
                    {p.category}
                  </span>
                )}
                {!DIAGNOSE_SHOTS.includes(p.category) && (
                <select
                  data-testid={`extra-photo-label-${p.id}`}
                  aria-label="Which area is this photo?"
                  value={AREA_OPTIONS.includes(p.category) ? p.category : ""}
                  onChange={(e) => setLabel(p.id, e.target.value)}
                  className="block w-full border-t border-slate-200 bg-white text-[11px] text-slate-700 px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-400"
                >
                  <option value="" disabled>Area…</option>
                  {AREA_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                )}
                <button
                  data-testid={`extra-photo-remove-${p.id}`}
                  onClick={() => remove(p.id)}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"
                  aria-label="Remove photo"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button
              data-testid="extra-photos-add"
              onClick={() => extraInput.current?.click()}
              disabled={busy}
              className="rounded-xl aspect-square border-2 border-dashed border-sky-300 bg-white hover:bg-sky-50 transition grid place-items-center text-sky-700"
            >
              <ImagePlus size={28} />
            </button>
          </div>
        </section>

        {busy && (
          <div className="mt-4 inline-flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="animate-spin" size={16} /> Uploading…
          </div>
        )}

        <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5" />
          <span>
            Your photos are kept at full quality for GreenFlow's review — no edits to what you captured.
          </span>
        </div>

        <Button
          data-testid="upload-continue-button"
          onClick={next}
          disabled={busy || starting}
          className={`mt-8 w-full h-12 text-base font-semibold rounded-xl ${toneBtn}`}
        >
          {starting ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
          Get My Assessment <ArrowRight className="ml-2" size={18} />
        </Button>
      </main>
    </div>
  );
}
